/**
 * Renewing a Cognito sign-in: one unauthenticated JSON POST to the pool's
 * regional endpoint (`InitiateAuth`, `REFRESH_TOKEN_AUTH`), over an injected
 * fetch — no library, no SRP.
 */
import { z } from 'zod';
import type { CognitoClientConfig } from './srp.js';

/** The subset of `fetch` a refresh uses; the browser's and Node's global `fetch` both satisfy it. */
export type FetchLike = (
  url: string,
  init: { method: string; headers: Record<string, string>; body: string },
) => Promise<{ status: number; text: () => Promise<string> }>;

/** What a refresh renews. Cognito keeps the refresh token valid and sends no new one. */
export type RefreshedTokens = { readonly idToken: string; readonly accessToken: string };

const refreshResponseSchema = z
  .object({
    AuthenticationResult: z.object({ IdToken: z.string().min(1), AccessToken: z.string().min(1) }).loose(),
  })
  .loose();

/**
 * Cognito's refusals of a refresh token: the sign-in is over and the user
 * signs in again. Anything else — throttling, a 5xx, the network — is
 * transient and THROWS, so a caller can keep its session and retry.
 */
const REFUSALS = new Set(['NotAuthorizedException', 'UserNotFoundException', 'UserNotConfirmedException', 'PasswordResetRequiredException']);

const errorTypeSchema = z.object({ __type: z.string() }).loose();

/**
 * Renew with `refreshToken`: the new ID and access tokens, null when Cognito
 * refuses the refresh token (revoked, expired, the user gone), or a rejection
 * when the attempt failed transiently.
 */
export async function refreshCognitoTokens(fetch: FetchLike, config: CognitoClientConfig, refreshToken: string): Promise<RefreshedTokens | null> {
  const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-amz-json-1.1', 'x-amz-target': 'AWSCognitoIdentityProviderService.InitiateAuth' },
    body: JSON.stringify({ AuthFlow: 'REFRESH_TOKEN_AUTH', ClientId: config.clientId, AuthParameters: { REFRESH_TOKEN: refreshToken } }),
  });
  const text = await response.text();
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    json = null;
  }
  if (response.status !== 200) {
    const type = errorTypeSchema.safeParse(json);
    // The type may be namespaced (`…#NotAuthorizedException`).
    const name = type.success ? (type.data.__type.split('#').pop() ?? '') : '';
    if (response.status === 400 && REFUSALS.has(name)) return null;
    throw new Error(`Cognito refresh failed (HTTP ${String(response.status)}${name === '' ? '' : ` ${name}`}); the session is kept`);
  }
  const parsed = refreshResponseSchema.safeParse(json);
  if (!parsed.success) throw new Error('Cognito answered a refresh without tokens');
  const result = parsed.data.AuthenticationResult;
  return { idToken: result.IdToken, accessToken: result.AccessToken };
}
